import * as React from "react";

import { useGetSupplierFamilyAuditLogQuery } from "@/api/suppliers";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { ContractFamilyAuditEvent, SupplierFamilySummary } from "@/types/supplier";

const PAGE_SIZE = 50;

const METADATA_FIELD_LABELS: Record<string, string> = {
  name: "Name",
  external_id: "External ID",
  estimated_total_contract_value: "Total value",
  estimated_annual_contract_value: "Annual value",
  currency_code: "Currency",
  procurement_status: "Status",
  expiry_date: "Expiry date",
  divisions: "Divisions",
  owner_user_id: "Owner",
  contact_name: "Contact name",
  contact_email: "Contact email",
  contact_phone: "Contact phone",
};

const describeEvent = (event: ContractFamilyAuditEvent): string => {
  switch (event.action) {
    case "attached":
      return "Attached the Contract Family to this supplier";
    case "detached":
      return "Detached the Contract Family from this supplier";
    case "metadata_updated":
      return "Edited metadata";
    case "document_added":
      return `Added document “${event.document_name ?? "Unknown document"}”`;
    case "document_removed":
      return `Removed document “${event.document_name ?? "Unknown document"}”`;
    case "document_renamed":
      return `Renamed a document to “${event.document_name ?? "Unknown document"}”`;
    case "summary_edited":
      return "Edited the Contract Summary";
    case "summary_update_requested":
      return "Requested a Contract Summary update";
  }
};

// Summary field names come from the organisation's template, so they are shown as stored.
const fieldLabel = (event: ContractFamilyAuditEvent, field: string) =>
  event.action === "metadata_updated" ? (METADATA_FIELD_LABELS[field] ?? field) : field;

interface Props {
  supplierId: string;
  family: SupplierFamilySummary;
  onClose: () => void;
}

// Mounted only while open, so every opening starts from the first page.
export const SupplierFamilyAuditLogDialog = ({ supplierId, family, onClose }: Props) => (
  <Dialog open onOpenChange={open => !open && onClose()}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Audit log</DialogTitle>
        <DialogDescription>Changes made to {family.name}, newest first.</DialogDescription>
      </DialogHeader>
      <AuditLogEntries supplierId={supplierId} familyId={family.id} />
    </DialogContent>
  </Dialog>
);

interface AuditLogEntriesProps {
  supplierId: string;
  familyId: string;
}

const AuditLogEntries = ({ supplierId, familyId }: AuditLogEntriesProps) => {
  const [offset, setOffset] = React.useState(0);
  const [loadedEvents, setLoadedEvents] = React.useState<ContractFamilyAuditEvent[]>([]);
  const { currentData, isLoading, isFetching, isError, refetch } = useGetSupplierFamilyAuditLogQuery(
    { supplierId, familyId, params: { offset, limit: PAGE_SIZE } },
    // Documents and summary cells change outside this page; every opening reads the log afresh.
    { refetchOnMountOrArgChange: true },
  );
  const pageItems = currentData?.items;
  const pageOffset = currentData?.offset;
  const total = currentData?.total ?? 0;
  const actorIds = React.useMemo(
    () => [...new Set(loadedEvents.flatMap(event => (event.actor_user_id ? [event.actor_user_id] : [])))],
    [loadedEvents],
  );
  const { data: actors = [] } = useGetDirectoryUsersQuery(actorIds, { skip: actorIds.length === 0 });
  const actorsById = React.useMemo(() => new Map(actors.map(actor => [actor.id, actor])), [actors]);
  const { formatDateTime } = useDateFormatter();

  React.useEffect(() => {
    if (!pageItems || pageOffset === undefined) return;
    setLoadedEvents(current => {
      if (pageOffset === 0) return pageItems;
      const existingIds = new Set(current.map(event => event.id));
      return [...current, ...pageItems.filter(event => !existingIds.has(event.id))];
    });
  }, [pageItems, pageOffset]);

  const actorName = (actorUserId: string | null) => {
    if (!actorUserId) return "System";
    const actor = actorsById.get(actorUserId);
    return actor ? `${actor.name} ${actor.surname}`.trim() || actor.email : "Unknown user";
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading audit log…</p>;

  return (
    <div className="max-h-[60vh] overflow-y-auto">
      {isError ? (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-2">
          <p role="alert" className="text-sm text-red-700">
            The audit log could not be loaded.
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : null}
      {!isError && loadedEvents.length === 0 ? (
        <p className="rounded-md border border-dashed border-[#ddd4ca] bg-[#fbf8f4] px-4 py-5 text-sm text-gray-500">
          No changes have been recorded for this Contract Family yet.
        </p>
      ) : null}
      {loadedEvents.length > 0 ? (
        <ol aria-label="Audit log entries" className="space-y-2">
          {loadedEvents.map(event => (
            <li key={event.id} className="rounded-md border border-[#ebe5de] bg-white px-4 py-3">
              <p className="break-words text-sm font-medium text-gray-900">{describeEvent(event)}</p>
              {event.changed_fields.length > 0 ? (
                <p className="mt-1 break-words text-sm text-gray-700">
                  Fields: {event.changed_fields.map(field => fieldLabel(event, field)).join(", ")}
                </p>
              ) : null}
              <p className="mt-1 text-xs font-medium text-gray-500">
                {actorName(event.actor_user_id)} ·{" "}
                <time dateTime={event.created_at}>{formatDateTime(event.created_at)}</time>
              </p>
            </li>
          ))}
        </ol>
      ) : null}
      {!isError && loadedEvents.length < total ? (
        <div className="mt-3 flex justify-center">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={isFetching}
            onClick={() => setOffset(loadedEvents.length)}
          >
            {isFetching ? "Loading…" : "Load older changes"}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
